// PJC v10 — navigation : fermeture du bandeau d'annonce et accessibilité du tiroir mobile.
// Chargé en module par sections/header.liquid. Idempotent : un module n'est évalué qu'une fois, et les
// écouteurs globaux sont protégés par un drapeau au cas où le fichier serait chargé sous deux URL.

const STORAGE_PREFIX = 'pjc-ann:';
const READY = Symbol.for('pjc.nav.ready');

/** @param {string} key */
function isClosed(key) {
  try {
    return window.sessionStorage.getItem(STORAGE_PREFIX + key) === '1';
  } catch {
    return false;
  }
}

/** @param {string} key */
function rememberClosed(key) {
  try {
    window.sessionStorage.setItem(STORAGE_PREFIX + key, '1');
  } catch {
    // Stockage indisponible (navigation privée, données bloquées) : la fermeture ne vaut que pour cette page.
  }
}

/**
 * Bandeau d'annonce fermable. La clé (data-key) change quand le texte change : un nouveau message réapparaît.
 * Dans l'éditeur de thème, le bandeau n'est jamais masqué pour rester modifiable.
 */
class PjcAnnouncement extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);

    const key = this.dataset.key ?? '';
    const inEditor = Boolean(window.Shopify && window.Shopify.designMode);
    const closed = !inEditor && key !== '' && isClosed(key);

    this.hidden = closed;
    if (!closed) document.documentElement.removeAttribute('data-pjc-ann-closed');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('[data-pjc-announcement-close]')) return;

    rememberClosed(this.dataset.key ?? '');
    this.hidden = true;

    // Le bouton qui avait le focus disparaît : on rend le focus au premier lien de l'en-tête.
    const next = document.querySelector('#header-component a[href], #header-component summary');
    if (next instanceof HTMLElement) next.focus({ preventScroll: true });
  };
}

if (!customElements.get('pjc-announcement')) {
  customElements.define('pjc-announcement', PjcAnnouncement);
}

/**
 * Libellé du bouton « Menu » : devient « Fermer le menu » tant que le tiroir est ouvert.
 * @param {HTMLElement | null} summary
 * @param {'open' | 'close'} state
 */
function setSummaryLabel(summary, state) {
  if (!summary) return;
  const label = state === 'close' ? summary.dataset.pjcLabelClose : summary.dataset.pjcLabelOpen;
  if (label) summary.setAttribute('aria-label', label);
}

/**
 * Place le focus sur le bouton Fermer une fois le tiroir visible (la visibilité est animée : on réessaie brièvement).
 * @param {HTMLDetailsElement} details
 * @param {number} tries
 */
function focusDrawerClose(details, tries) {
  window.setTimeout(() => {
    if (!details.open) return;
    const close = details.querySelector('.menu-drawer__close-button');
    if (!(close instanceof HTMLElement)) return;

    close.focus({ preventScroll: true });
    if (document.activeElement !== close && tries > 0) focusDrawerClose(details, tries - 1);
  }, 60);
}

/** @param {Event} event */
function onDrawerToggle(event) {
  const details = event.target;
  if (!(details instanceof HTMLDetailsElement) || !details.matches('.menu-drawer-container')) return;

  const summary = details.querySelector(':scope > summary');
  const trigger = summary instanceof HTMLElement ? summary : null;

  if (details.open) {
    setSummaryLabel(trigger, 'close');
    focusDrawerClose(details, 6);
    return;
  }

  setSummaryLabel(trigger, 'open');

  // Fermeture : si le focus était dans le tiroir (ou perdu), il revient sur le bouton « Menu ».
  const active = document.activeElement;
  if (trigger && (!active || active === document.body || details.contains(active))) {
    trigger.focus({ preventScroll: true });
  }
}

if (!(READY in window)) {
  Object.defineProperty(window, READY, { value: true });
  // L'événement toggle ne remonte pas : écoute en phase de capture sur le document.
  document.addEventListener('toggle', onDrawerToggle, true);
}
